package dev.lingvo.site.ds

import androidx.compose.runtime.Composable
import androidx.compose.runtime.remember
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.css.WhiteSpace
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.alignItems
import com.varabyte.kobweb.compose.ui.modifiers.attr
import com.varabyte.kobweb.compose.ui.modifiers.backgroundColor
import com.varabyte.kobweb.compose.ui.modifiers.border
import com.varabyte.kobweb.compose.ui.modifiers.borderRadius
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.cursor
import com.varabyte.kobweb.compose.ui.modifiers.display
import com.varabyte.kobweb.compose.ui.modifiers.flexGrow
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.modifiers.height
import com.varabyte.kobweb.compose.ui.modifiers.lineHeight
import com.varabyte.kobweb.compose.ui.modifiers.maxWidth
import com.varabyte.kobweb.compose.ui.modifiers.minWidth
import com.varabyte.kobweb.compose.ui.modifiers.outline
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.modifiers.position
import com.varabyte.kobweb.compose.ui.modifiers.whiteSpace
import com.varabyte.kobweb.compose.ui.modifiers.width
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.base
import com.varabyte.kobweb.silk.style.selectors.focusWithin
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import dev.lingvo.site.i18n.t
import kotlin.random.Random
import kotlinx.browser.document
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.attributes.disabled
import org.jetbrains.compose.web.css.AlignItems
import org.jetbrains.compose.web.css.DisplayStyle
import org.jetbrains.compose.web.css.LineStyle
import org.jetbrains.compose.web.css.Position
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Input
import org.jetbrains.compose.web.dom.Label
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text
import org.w3c.dom.HTMLInputElement

/** Drawn at its final orientation; nothing here is a rotated or flipped glyph (rule 2). */
/** What a bound reads as when it is unset. An absent date is absent, never a zeroed one (rule 11). */
private const val ABSENT_BOUND = "—"

/**
 * The message for a range whose bounds are the wrong way round, or `null` when they are not.
 *
 * Public because the caller usually owns something that must not fire on an inverted range — the
 * CSV export link that carries the range, the query behind the table — and that decision should
 * come from the same rule the control renders, not from a second copy of it.
 *
 * ISO-8601 dates are fixed-width and zero-padded, so lexicographic order *is* chronological order
 * and no parsing is needed. An empty bound is not yet a claim about order, so it is not an error.
 */
fun dateRangeErrorOrNull(from: String, to: String): String? =
    if (from.isNotEmpty() && to.isNotEmpty() && from > to) {
        "$from is after $to. A range starts on or before it ends."
    } else {
        null
    }

/**
 * The frame. `borderStrong`, because this is a control and `border` is a 1.32:1 divider (rule 10).
 *
 * `surface`, not `surfaceBase`. The spec gives this control the card fill, and a control filled
 * with the page's own ground is an outline rather than a field. The boundary clears the 3:1 floor
 * from inside as well as out: `borderStrong` on `surface` is **3.94** in Day and **3.66** in Night,
 * and 3.67 / 3.93 against the page ground the frame is drawn on. Computed, not read off the
 * per-mode comments in `Tokens.kt` — Day's second column there is `surfaceSunken`, not `surface`.
 *
 * Width is 100% because the 320 lives on [LDateRange]'s column instead. The caption and the error
 * belong to this control and have to end where it ends, not run the width of whatever contains it.
 *
 * An inverted range does **not** tint this boundary: status colour never touches an interactive
 * control (rule 4). The error is carried by its own sentence beneath, where the word does the work.
 */
val LDateRangeStyle = CssStyle {
    base {
        Modifier
            .width(100.percent)
            .height(ControlHeight.MD)
            .backgroundColor(colorMode.toSitePalette().surface)
            .border(1.px, LineStyle.Solid, colorMode.toSitePalette().borderStrong)
            .borderRadius(Radius.Control)
            .padding(leftRight = Space.MD)
            .gap(Space.SM)
            .transitionColors()
    }
    focusWithin {
        Modifier
            .outline(2.px, LineStyle.Solid, colorMode.toSitePalette().accent)
            .styleModifier { property("outline-offset", "2px") }
    }
    // No visible label sibling — `label` is only the `role="group"` aria-label — so the floor goes
    // directly on this, the whole control's own outermost element.
    cssRule("[data-disabled=\"true\"]") { Modifier.disabledSurface() }
}

/**
 * One bound's layout box. The 40px hit box is the input's — see [LDateRangeInputStyle].
 *
 * **The underline marks which bound you are in, and it is not decoration.** The control has two tab
 * stops and [LDateRangeStyle] draws a single `:focus-within` ring around the whole of it, so tabbing
 * from the start date to the end date changed nothing on screen: the same box, outlined the same
 * way, while the keystrokes moved to the other date. A reader correcting one bound of a billing
 * period had no way to see which bound they were correcting.
 *
 * An underline rather than a tint or a colour change, for three reasons. It adds a shape instead of
 * recolouring one, so it does not carry the distinction on colour alone (WCAG 1.4.1). It leaves the
 * mono value's metrics untouched, so nothing reflows and the arrow between the bounds does not
 * shift. And it is the accent-under-the-active-thing vocabulary the tab strip already uses, so it
 * needs no learning.
 */
val LDateRangeFieldStyle = CssStyle {
    base {
        Modifier
            .position(Position.Relative)
            .display(DisplayStyle.Flex)
            .alignItems(AlignItems.Center)
            .flexGrow(1)
            .height(100.percent)
            .minWidth(0.px)
    }
    focusWithin {
        Modifier.styleModifier {
            property("box-shadow", "inset 0 -2px 0 ${colorMode.toSitePalette().accent}")
        }
    }
}

/**
 * The native control, covering its bound entirely and painted at zero opacity.
 *
 * `opacity` rather than `display: none` or `visibility: hidden`: those two would take the input out
 * of the accessibility tree and out of the tab order, which is the whole reason it is here.
 *
 * `inset: -1px 0` and no `height`, rather than `inset: 0` with `height: 100%`. The control is 40
 * tall *including* its hairline, so a child confined to the padding box is 38 and misses the 40px
 * target floor the spec states for exactly these two fields. Pulling the input out over the border
 * on both edges makes the hit box a true 40 and moves nothing, because it is already out of flow.
 */
val LDateRangeInputStyle = CssStyle.base {
    Modifier
        .position(Position.Absolute)
        .width(100.percent)
        .cursor(Cursor.Pointer)
        .backgroundColor(Colors.Transparent)
        .styleModifier {
            property("inset", "-1px 0")
            property("opacity", "0")
            property("border", "0")
            property("padding", "0")
            property("appearance", "none")
            property("min-width", "0")
        }
}

/**
 * The ISO rendering of a bound.
 *
 * `<input type="date">` paints its value in the visitor's locale — `08/01/2026` in en-US, and a
 * different order again in en-GB — so the native rendering is thrown away and this one is shown.
 * These two dates get reconciled against an invoice, and a date whose field order you have to infer
 * from your own locale is not a figure you can reconcile.
 */
val LDateRangeValueStyle = CssStyle.base {
    Modifier
        .color(colorMode.toSitePalette().textPrimary)
        .fontSize(TypeScale.Small)
        .lineHeight(TypeScale.BodyLineHeight)
        .monoFont()
        .tabularNumbers()
        .whiteSpace(WhiteSpace.NoWrap)
}

val LDateRangeArrowStyle = CssStyle.base {
    Modifier
        .color(colorMode.toSitePalette().textMuted)
        .iconSize(IconSize.MD)
        .styleModifier { property("flex-shrink", "0") }
}

val LDateRangeCaptionStyle = CssStyle.base {
    Modifier
        .color(colorMode.toSitePalette().textMuted)
        .fontSize(TypeScale.Micro)
        .fontWeight(500)
        .lineHeight(TypeScale.BodyLineHeight)
}

/**
 * A from/to range over a billing or usage period.
 *
 * Two real `<input type="date">` elements are the control, so the platform picker, the arrow-key
 * segment editing and the screen-reader announcement all come for free; the mono ISO text is only
 * their presentation. [onChange] emits both bounds as ISO strings whichever one moved.
 *
 * The caption is **permanent**, not a tooltip and not a hover. Whether the upper bound is inside the
 * period changes the answer by a day at a period boundary, and a range whose bounds you have to
 * guess is a range you cannot reconcile against an invoice — so the convention is on screen at all
 * times, for everybody, including the person reading a printout.
 *
 * [modifier] sizes the whole control — the frame, the caption and the error sentence together, so
 * `Modifier.width(100.percent)` is what makes it go full width on a narrow viewport. It is applied
 * after the default 320, so a caller's width wins.
 *
 * An inverted range is reported, never repaired: swapping the bounds silently would hand back a
 * period the reader did not ask for and would look, from the outside, exactly like a range that had
 * been accepted. [error] overrides the built-in message when the server has a better one.
 */
@Composable
fun LDateRange(
    from: String,
    to: String,
    onChange: (from: String, to: String) -> Unit,
    modifier: Modifier = Modifier,
    label: String = t("ds.ds.ldate_range.date_range", "Date range"),
    fromLabel: String = t("ds.ds.ldate_range.from_date_included", "From date, included"),
    toLabel: String = t("ds.ds.ldate_range.to_date_not_included", "To date, not included"),
    caption: String = t("ds.ds.ldate_range.bounds_caption", "From is included, to is not. All UTC."),
    error: String? = null,
    enabled: Boolean = true,
) {
    val scope = remember { "lingvo-range-${Random.nextInt(1_000_000)}" }
    val fromId = "$scope-from"
    val toId = "$scope-to"
    val captionId = "$scope-caption"
    val errorId = "$scope-error"
    val message = error ?: dateRangeErrorOrNull(from, to)
    // Both inputs point at the caption, so the convention is announced on whichever bound is entered.
    val describedBy = if (message != null) "$captionId $errorId" else captionId

    Column(
        Modifier
            .width(320.px)
            .maxWidth(100.percent)
            .then(modifier)
            .gap(Space.SM),
    ) {
        Row(
            LDateRangeStyle.toModifier()
                .attr("data-disabled", (!enabled).toString())
                .attr("role", "group")
                .attr("aria-label", label),
            verticalAlignment = Alignment.CenterVertically,
        ) {
            LDateRangeCalendarGlyph(Modifier.color(ColorMode.current.toSitePalette().textMuted))
            LDateRangeBound(
                id = fromId,
                label = fromLabel,
                value = from,
                describedBy = describedBy,
                invalid = message != null,
                enabled = enabled,
                onValueChange = { onChange(it, to) },
            )
            LArrowRightIcon(LDateRangeArrowStyle.toModifier().attr("data-directional", "inline"))
            LDateRangeBound(
                id = toId,
                label = toLabel,
                value = to,
                describedBy = describedBy,
                invalid = message != null,
                enabled = enabled,
                onValueChange = { onChange(from, it) },
            )
        }
        Div(LDateRangeCaptionStyle.toModifier().toAttrs { id(captionId) }) {
            Text(caption)
        }
        if (message != null) {
            Div(
                LFieldErrorStyle.toModifier().toAttrs {
                    id(errorId)
                    attr("role", "alert")
                },
            ) {
                Text(message)
            }
        }
    }
}

@Composable
private fun LDateRangeBound(
    id: String,
    label: String,
    value: String,
    describedBy: String,
    invalid: Boolean,
    enabled: Boolean,
    onValueChange: (String) -> Unit,
) {
    Div(LDateRangeFieldStyle.toModifier().toAttrs()) {
        Label(forId = id, attrs = LVisuallyHiddenStyle.toModifier().toAttrs()) {
            Text(label)
        }
        // Hidden from assistive tech: the input beneath already carries this value, and a screen
        // reader reading the date twice is worse than reading it once.
        Span(LDateRangeValueStyle.toModifier().toAttrs { attr("aria-hidden", "true") }) {
            Text(value.ifEmpty { ABSENT_BOUND })
        }
        Input(
            type = InputType.Date,
            attrs = LDateRangeInputStyle.toModifier().toAttrs {
                id(id)
                value(value)
                attr("aria-describedby", describedBy)
                if (invalid) attr("aria-invalid", "true")
                if (!enabled) disabled()
                onInput { onValueChange(it.value) }
                onClick { (document.getElementById(id) as? HTMLInputElement)?.openPicker() }
            },
        )
    }
}

/**
 * Opens the platform picker from anywhere in the bound's 40px hit box.
 *
 * Chrome and Safari open the picker only from the native calendar indicator, which this control
 * paints away along with the rest of the input's own rendering. `showPicker` is absent from the
 * Kotlin DOM typings and throws where the browser does not credit a user gesture — either way the
 * field still accepts a typed or arrow-keyed date, so a failure here costs nothing.
 */
private fun HTMLInputElement.openPicker() {
    runCatching { asDynamic().showPicker() }
}

@Composable
private fun LDateRangeCalendarGlyph(modifier: Modifier = Modifier) {
    LCalendarIcon(
        Modifier
            .iconSize(IconSize.MD)
            .styleModifier { property("flex-shrink", "0") }
            .then(modifier),
    )
}
